import type { UseAllergySelectionResult } from '@/features/rx-form/model/useAllergySelection';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/shadcn/command';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/shared/ui/shadcn/field';
import { useState } from 'react';

export interface AllergySelectorProps {
  selection: UseAllergySelectionResult;
}

export function AllergySelector({ selection }: AllergySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);

  const {
    addAllergy,
    addError,
    isSearchTextValid,
    pendingAddAllergenId,
    searchQuery,
    searchResults,
    searchText,
    searchTextError,
    setSearchText,
  } = selection;

  const isAdding = pendingAddAllergenId !== null;

  return (
    <FieldGroup>
      <Field>
        <FieldContent>
          <FieldLabel>Add allergy</FieldLabel>
          <FieldDescription>
            {searchText.trim().length > 0 && !isSearchTextValid
              ? (searchTextError ?? 'Type at least 3 characters to search allergens.')
              : searchQuery.isError
                ? 'Search failed.'
                : 'Search DoseSpot allergens'}
          </FieldDescription>
        </FieldContent>

        <div className="flex w-full flex-col gap-2">
          <Button
            type="button"
            variant="secondary"
            className="w-full justify-start"
            onClick={() => setIsOpen(true)}
            disabled={isAdding}
            aria-busy={isAdding}
          >
            {isAdding ? 'Adding allergy…' : 'Search allergens'}
          </Button>

          <CommandDialog
            open={isOpen}
            onOpenChange={(open) => {
              setIsOpen(open);
              if (!open) {
                setSearchText('');
              }
            }}
            title="Search allergens"
            description="Type to search DoseSpot allergens."
          >
            <CommandInput
              value={searchText}
              onValueChange={setSearchText}
              placeholder="Search allergens..."
              autoFocus
            />
            <CommandList>
              {searchText.trim().length === 0 ? (
                <CommandEmpty>Type an allergen name to search.</CommandEmpty>
              ) : !isSearchTextValid ? (
                <CommandEmpty>
                  {searchTextError ?? 'Type at least 3 characters to search allergens.'}
                </CommandEmpty>
              ) : isAdding ? (
                <CommandEmpty>Adding allergy…</CommandEmpty>
              ) : searchQuery.isPending ? (
                <CommandEmpty>Searching…</CommandEmpty>
              ) : searchQuery.isError ? (
                <CommandEmpty>
                  Search failed.
                  <div className="mt-3 flex justify-center">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => void searchQuery.refetch()}
                    >
                      Retry
                    </Button>
                  </div>
                </CommandEmpty>
              ) : searchResults.length === 0 ? (
                <CommandEmpty>No allergens found.</CommandEmpty>
              ) : (
                <CommandGroup heading="Results">
                  {searchResults.map((r) => (
                    <CommandItem
                      key={`${r.allergenId ?? 'none'}-${r.label}`}
                      value={`${r.label} ${r.allergenType}`}
                      disabled={r.allergenId === null || pendingAddAllergenId === r.allergenId}
                      onSelect={() => void addAllergy(r)}
                    >
                      <div className="flex min-w-0 flex-col">
                        <div className="truncate">
                          {r.label}
                          {!r.isActive ? ' (inactive)' : ''}
                        </div>
                        <div className="text-muted-foreground truncate text-xs">
                          {r.allergenType}
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </CommandDialog>

          {addError ? (
            <Alert variant="destructive">
              <AlertTitle>Could not add allergy</AlertTitle>
              <AlertDescription>{addError}</AlertDescription>
            </Alert>
          ) : null}
        </div>
      </Field>
    </FieldGroup>
  );
}
